import { getErrorMessage } from '@repo/lib'
import { ColourPalette, TriviaList } from '@repo/types'
import { Button, Container, Label } from '@repo/ui'
import { useSuccessButton } from '@repo/ui/client'
import { Field, Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'

import { AppHeader } from '@/components/AppHeader'
import { AppHeaderLogo } from '@/components/AppHeaderLogo'
import { ColourPaletteField } from '@/components/ColourPaletteSelector'
import { FormError } from '@/components/forms/FormError'
import { TextField } from '@/components/forms/TextField'
import { BackButton } from '@/components/icon-buttons/IconButtons'
import { Main } from '@/components/Main'
import { Page } from '@/components/Page'
import { PageTitle } from '@/components/PageTitle'
import { TermsOfService } from '@/components/TermsOfService'
import { ImageUpload } from '@/pages/settings/components/ImageUpload'

interface Props {
  baseUrl?: string
  createTriviaList: (values: TriviaList) => Promise<{ listId: string }>
  imageStorageFolder: string
}

export const CreateTriviaListView = observer(function CreateTriviaListView({
  baseUrl = '/',
  createTriviaList,
  imageStorageFolder
}: Props): React.ReactElement {
  const [formError, setFormError] = useState<string | undefined>()
  const { showSuccess, showSuccessButtonState } = useSuccessButton()
  const history = useHistory()

  const handleSubmit = async (values: TriviaList) => {
    try {
      const { listId } = await createTriviaList(values)
      await showSuccessButtonState()

      history.push(`${baseUrl}trivia/lists/edit/${listId}`)
    } catch (error) {
      setFormError(getErrorMessage(error))
    }
  }

  return (
    <Page title='Create a Custom Trivia List'>
      <AppHeader
        actionsLeft={<BackButton url={`${baseUrl}trivia/lists`} />}
        header={<AppHeaderLogo />}
      />

      <Main>
        <Container size='medium'>
          <PageTitle className='mb-6 text-center' size='large' disableTruncation>
            Create a Custom Trivia List
          </PageTitle>

          <Formik
            initialValues={{
              colour: ColourPalette.PURPLE,
              id: '',
              items: [],
              title: 'New Trivia List'
            }}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, setFieldValue }) => (
              <Form
                className='flex flex-col gap-8'
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <div className='flex flex-col gap-6'>
                  <TextField label='Title' name='title' autoFocus />
                </div>

                <div className='flex flex-col gap-2'>
                  <Label>Colour</Label>
                  <ColourPaletteField />
                </div>

                <div className='flex flex-col gap-2'>
                  <Label>Image</Label>
                  <Field name='image.url' type='hidden' />
                  <ImageUpload
                    storageFolder={imageStorageFolder}
                    onRemoveImage={() => {
                      setFieldValue('image.url', '')
                    }}
                    onUploadSuccess={({ url }) => {
                      setFieldValue('image.url', url)
                    }}
                  />
                </div>

                <Button
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  showSuccess={showSuccess}
                  successText='Created'
                  text='Create'
                  type='submit'
                />

                {formError && <FormError error={formError} />}
              </Form>
            )}
          </Formik>
        </Container>

        <TermsOfService />
      </Main>
    </Page>
  )
})
